/**
 * This allows the iFrame created by the Chrome Extension to make authenticated requests to the
 * Readlang server, even if the user has 3rd party cookies disabled.
 *
 * Flow is as follows:
 *
 * 1. This module's request() function is called, which sends a message to the extension content
 *    script, including all the request data and a request ID.
 * 2. Content script receives the message, and forwards the request data to the background script.
 * 3. Background script makes authenticated request to Readlang server, including cookies.
 * 4. Background script sends response back to content script.
 * 5. Content scripts sends response back to this module as a message including the request ID.
 * 6. This module resolves the promise with the response data.
 */
import { messageListener, postMessage } from "messagingUtils";

interface RequestOptions {
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  url: string;
  data?: any;
}

interface Response {
  status: number;
  data?: any;
  statusText?: any;
}

let requestId = 1;
const uniquePID = Math.random().toString(36).substr(2, 9);

const requestIdToResolveRejectMap = new Map<
  string,
  { resolve: any; reject: any }
>();

export const request = (options: RequestOptions) =>
  new Promise((resolve, reject) => {
    const thisRequestId = requestId++;

    // This will be received by the content script which is injected into the readlang.com iframe
    // by the Chrome Extension.
    postMessage(
      "extension",
      "serverRequest",
      {
        method: options.method,
        url: options.url,
        data: options.data,
      },
      `${uniquePID}:${thisRequestId}`
    );

    requestIdToResolveRejectMap.set(`${uniquePID}:${thisRequestId}`, {
      resolve,
      reject,
    });
  });

export const responseReceived = (
  fullRequestId: string /* includes PID */,
  response: Response
) => {
  const callbacks = requestIdToResolveRejectMap.get(fullRequestId);

  if (!callbacks) {
    throw new Error(
      "responseReceived called with unknown or already resolved requestId: " +
        fullRequestId
    );
  }

  if (response.status === 200) {
    callbacks.resolve(response);
  } else {
    callbacks.reject(response);
  }
};

messageListener("webReaderIFrame", (message, data) => {
  switch (message) {
    case "backgroundPageResponse": {
      responseReceived(data.requestId, {
        status: data.response.status,
        data: data.response.data,
        statusText: data.response.statusText,
      });
      break;
    }
  }
});
